import React from 'react';
import { Alert, Box, Button, Chip, CircularProgress, Stack, Typography } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import ResponsiveTable, { type ResponsiveTableColumn } from '../common/ResponsiveTable';
import supplierApi, { supplierError } from '../../services/supplierApi';
import type { CreationRow } from '../../types/suppliers';

/** Petty Cash > Suppliers > Created through PRPO: every vendor PRPO created (or tried to) in the company. */

const formatDate = (value: string): string =>
  value ? new Date(value).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }) : '';

const sourceText = (r: CreationRow): string => {
  if (r.source === 'Excel') return `Excel${r.fileName ? `: ${r.fileName}` : ''}${r.rowNum ? `, row ${r.rowNum}` : ''}`;
  if (r.source === 'Claim') return 'Petty cash line';
  return 'Form';
};

const statusChip = (r: CreationRow) => {
  if (r.status === 'Created') return <Chip size="small" color="success" label="Created" />;
  if (r.status === 'Failed') return <Chip size="small" color="error" label="SAP refused" />;
  if (r.interrupted) return <Chip size="small" color="warning" label="Interrupted: check SAP" />;
  return <Chip size="small" color="info" label="Creating…" />;
};

const SupplierLog: React.FC<{ companyId: number; refreshKey?: number }> = ({ companyId, refreshKey = 0 }) => {
  const [rows, setRows] = React.useState<CreationRow[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState('');
  const [tick, setTick] = React.useState(0);

  React.useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');
    supplierApi.log(companyId)
      .then((r) => { if (!cancelled) setRows(r); })
      .catch((e) => { if (!cancelled) setError(supplierError(e, 'The created suppliers could not be read')); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [companyId, refreshKey, tick]);

  const columns: ResponsiveTableColumn<CreationRow>[] = [
    { id: 'date', header: 'Date', cell: (r) => formatDate(r.createdAt), width: 170 },
    {
      id: 'supplier',
      header: 'Supplier',
      cell: (r) => (
        <Box>
          <Typography variant="body2" fontWeight={600}>{r.cardName}</Typography>
          {r.vat ? <Typography variant="caption" color="text.secondary">VAT {r.vat}</Typography> : null}
        </Box>
      ),
    },
    { id: 'code', header: 'SAP code', cell: (r) => r.cardCode || '', width: 110 },
    { id: 'status', header: 'Status', cell: statusChip, width: 170 },
    { id: 'source', header: 'From', cell: sourceText, hideOnMobile: true },
    { id: 'by', header: 'By', cell: (r) => r.createdBy || '', hideOnMobile: true },
    { id: 'detail', header: 'Detail', cell: (r) => r.error || '', hideOnMobile: true },
  ];

  return (
    <Stack spacing={2}>
      <Stack direction="row" spacing={1} alignItems="center">
        <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
          Every supplier PRPO created in this company, newest first. Vendors created directly in SAP B1 are not listed.
        </Typography>
        <Button variant="outlined" startIcon={loading ? <CircularProgress size={16} /> : <RefreshIcon />} onClick={() => setTick((t) => t + 1)} disabled={loading}>
          Refresh
        </Button>
      </Stack>
      {error ? <Alert severity="error">{error}</Alert> : null}
      <ResponsiveTable
        ariaLabel="Suppliers created through PRPO"
        columns={columns}
        rows={rows}
        getRowKey={(r) => r.creationId}
        emptyMessage={loading ? 'Loading…' : 'No supplier created through PRPO yet.'}
        size="small"
      />
    </Stack>
  );
};

export default SupplierLog;
